import { useState } from 'react';
import { Flag } from '@weave/schema-gen-ts/dist/schemas/feature-flags/feature_flags_types.pb';
import { useQuery } from 'react-query';
import { Icon } from '@frontend/icons';
import { SchemaFeatureFlags } from '@frontend/schema';
import { theme } from '@frontend/theme';
import { Table, Text, useModalControl } from '@frontend/design-system';
import { FeatureFlagTableFilters } from './feature-flag-table-filters';
import { BatchSetModal } from './modals/batch-set-modal';
import { DeleteFlagModal } from './modals/delete-flag-modal';
import { EditFlagModal } from './modals/edit-flag-modal';
import { LocationsEnabledModal } from './modals/locations-enabled-modal';

const MOCK_FLAG_DATA: Flag[] = [
  {
    id: '61b4aff9-faf2-44f7-a1a3-4c2eced0c6d7',
    name: 'Test 1',
    description: 'Test 1 Description',
    defaultValue: true,
  },

  {
    id: '8a45a367-5bd1-4666-9b4b-5f332e9d5ca3',
    name: 'Test 2',
    description: 'Test 2 Description',
    defaultValue: false,
  },
];

export const GlobalFeatureFlagTable = () => {
  // const queryClient = useQueryClient();
  const { modalProps: editFlagModalProps, openModal: openEditFlagModal } = useModalControl();
  const { modalProps: deleteFlagModalProps, openModal: openDeleteFlagModal } = useModalControl();
  const { modalProps: locationsEnabledModalProps, openModal: openLocationsEnabledModal } = useModalControl();
  const { modalProps: batchSetModalProps, openModal: openBatchSetModal } = useModalControl();
  // const { currentLocationId: locationId } = useLocationService();
  const [currentFeatureFlag, setCurrentFeatureFlag] = useState<Flag | undefined>(undefined);
  const { data: featureFlags, refetch: refecthFlagData } = useQuery({
    queryKey: ['feature-flags'],
    queryFn: () => {
      return SchemaFeatureFlags.ListFlags({});
    },
  });

  const flags = featureFlags?.flags || MOCK_FLAG_DATA;

  return (
    <>
      <Table
        data={flags || []}
        colConfig={[
          {
            Header: 'Feature Flag',
            id: 'featureFlag',
            accessor: (flag) => flag.name,
          },
          {
            Header: 'Description',
            id: 'description',
            accessor: (flag) => flag.description,
            minWidth: 350,
          },
          {
            Header: 'Product Owner',
            id: 'product-owner',
            cellRenderer: () => <Text>Unknown</Text>,
            maxWidth: 150,
          },
          {
            Header: 'Flag Status',
            id: 'flag-status',
            cellRenderer: () => (
              <div
                css={{
                  display: 'flex',
                  alignItems: 'center',
                  columnGap: theme.spacing(1),
                }}
              >
                <Icon name='clock-small' color='success' />
                <Text>Test</Text>
              </div>
            ),
            maxWidth: 125,
          },
          {
            Header: 'Flag Creator',
            id: 'flag-creator',
            cellRenderer: () => <Text>Unknown</Text>,
            maxWidth: 125,
          },
          {
            Header: 'Status',
            id: 'status',
            accessor: (flag) => (flag.defaultValue ? 'Show' : 'Hide'),
            cellRenderer: (defaultValue) => {
              const isShow = defaultValue === 'Show';
              return (
                <Text
                  css={{
                    backgroundColor: isShow ? theme.colors.success10 : theme.colors.critical10,
                    color: isShow ? theme.colors.success50 : theme.colors.critical50,
                    padding: theme.spacing(1, 3),
                    borderRadius: theme.borderRadius.small,
                    minWidth: 85,
                    textAlign: 'center',
                  }}
                  size='medium'
                >
                  {defaultValue}
                </Text>
              );
            },
            maxWidth: 125,
          },
        ]}
        rowActions={{
          actions: [
            {
              Icon: () => <Icon name='locations-small' />,
              label: 'Locations Enabled',
              onClick: (flag) => {
                setCurrentFeatureFlag(flag);
                openLocationsEnabledModal();
              },
            },
            {
              Icon: () => <Icon name='edit-small' />,
              label: 'Edit Flag',
              onClick: (flag) => {
                setCurrentFeatureFlag(flag);
                openEditFlagModal();
              },
            },
            {
              Icon: () => <Icon name='update-small' />,
              label: 'Batch Set',
              onClick: (flag) => {
                setCurrentFeatureFlag(flag);
                openBatchSetModal();
              },
            },
            {
              Icon: () => <Icon name='trash-small' />,
              label: 'Delete Flag',
              onClick: (flag) => {
                setCurrentFeatureFlag(flag);
                openDeleteFlagModal();
              },
            },
          ],
        }}
        isPaginated
        manualFilters
        manualFiltersRender={(modalProps, setShowNotification) => (
          <FeatureFlagTableFilters modalProps={modalProps} setShowNotificationBadge={setShowNotification} />
        )}
        hasFilterColumns
        hasGlobalSearch
        tableActions={[
          {
            Icon: () => <Icon name='update' />,
            label: 'Update',
            onClick: () => refecthFlagData(),
          },
          {
            Icon: () => <Icon name='external-link' />,
            label: 'Link',
            onClick: () => {
              console.log('Link');
            },
          },
        ]}
      ></Table>
      <EditFlagModal modalProps={editFlagModalProps} flag={currentFeatureFlag} />
      <DeleteFlagModal modalProps={deleteFlagModalProps} flag={currentFeatureFlag} />
      <LocationsEnabledModal modalProps={locationsEnabledModalProps} flag={currentFeatureFlag} />
      <BatchSetModal modalProps={batchSetModalProps} flag={currentFeatureFlag} />
    </>
  );
};

GlobalFeatureFlagTable.displayName = 'GlobalFeatureFlagTable';
